import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from "react";

export const UserContext = createContext();

export const UserContextProvider = ({ children }) => {

  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ⏱️ TIEMPO INACTIVIDAD (60 min)
  const INACTIVITY_LIMIT = 60 * 60 * 1000;

  // 🔥 FIX IMPORTANTE: persistencia real del timer
  const inactivityTimer = useRef(null);

  // ============================
  // DECODIFICAR TOKEN
  // ============================
  const decodeToken = useCallback((token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch {
      return null;
    }
  }, []);

  // ============================
  // LOGOUT GLOBAL
  // ============================
  const logout = useCallback(() => {
    localStorage.clear();
    setUser(null);
    setIsAuthenticated(false);
    if (!window.location.pathname.startsWith("/public")) {
       window.location.href = "/login";
}
  }, []);

  // ============================
  // VALIDAR TOKEN
  // ============================
  const checkTokenExpiration = useCallback(() => {
    const token = localStorage.getItem("token");

    if (!token) return;

    const decoded = decodeToken(token);

    if (!decoded?.exp) return;

    const now = Date.now() / 1000;

    if (decoded.exp < now) {
      logout();
    }
  }, [decodeToken, logout]);

  // ============================
  // RESET TIMER INACTIVIDAD
  // ============================
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }

    inactivityTimer.current = setTimeout(() => {
      logout();
    }, INACTIVITY_LIMIT);
  }, [logout]);

  // ============================
  // DETECTAR ACTIVIDAD
  // ============================
  const setupActivityListeners = useCallback(() => {
    const events = ["mousemove", "keydown", "click", "scroll"];

    events.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [resetInactivityTimer]);

  // ============================
  // INIT
  // ============================
  useEffect(() => {
    const token = localStorage.getItem("token");
    const username = localStorage.getItem("user_name");
    const role = localStorage.getItem("user_type");

    if (token) {
      setUser({ username, role });
      setIsAuthenticated(true);

      checkTokenExpiration();
      resetInactivityTimer();

      const cleanup = setupActivityListeners();

      const interval = setInterval(checkTokenExpiration, 60000);

      return () => {
        cleanup();
        clearInterval(interval);
      };
    }
  }, [
    checkTokenExpiration,
    resetInactivityTimer,
    setupActivityListeners
  ]);

  // ============================
  // LOGIN
  // ============================
  const login = useCallback((data) => {
    const role = (data.role || "").toLowerCase();

    localStorage.setItem("token", data.token);
    localStorage.setItem("user_name", data.username);
    localStorage.setItem("user_type", role);

    setUser({
      username: data.username,
      role
    });

    setIsAuthenticated(true);

    resetInactivityTimer();
  }, [resetInactivityTimer]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated,
        login,
        logout
      }}
    >
      {children}
    </UserContext.Provider>
  );
};